/**
 * SanPlumb — Geometría de tuberías, accesorios y aparatos sanitarios
 *
 * Origen de migración: legacy `sanplumb-mvp__1_.html` líneas 604-738.
 *
 * Cambios respecto al MVP:
 *   - Funciones tipadas con Vec3 / Diameter / System en lugar de any
 *   - mkSanitary: opacidad subida a 0.85 (antes 0.6) según decisión Fase 2
 *   - Llave de paso preserva su ensamblaje en U §VII tal cual, sin cambios
 *     geométricos (el contrato §VII es invariante)
 *
 * Convención de ejes (Three.js, consistente con SystemData):
 *   X = ancho del cuarto
 *   Y = altura (vertical)
 *   Z = profundidad
 *   Origen = esquina inferior izquierda del cuarto, NPT ±0.00
 */

import * as THREE from 'three';
import { COLORS } from './colors';
import type { Vec3, Diameter, System, Sanitary } from '../../types';

// ──────────────────────────────────────────────────────────────────
// Constantes de geometría
// ──────────────────────────────────────────────────────────────────

/**
 * Radios visuales por diámetro nominal de tubería.
 * Estos NO son los diámetros reales (que son menores) — son los radios
 * visualmente exagerados para que el grosor sea perceptible en la escala
 * del visor 3D. Sin embargo, las proporciones entre diámetros se preservan.
 */
const PIPE_RADIUS: Readonly<Record<Diameter, number>> = {
  '1/2"': 0.018,
  '3/4"': 0.026,
  '1"': 0.032,
  '1¼"': 0.038,
};

/** Opacidad de los aparatos sanitarios en el visor (decisión Fase 2). */
const SANITARY_OPACITY = 0.85;

function segmentBetween(
  start: THREE.Vector3,
  end: THREE.Vector3,
  radius: number,
  material: THREE.Material,
  segments = 12,
): THREE.Mesh | null {
  const dir = new THREE.Vector3().subVectors(end, start);
  const len = dir.length();
  if (len < 0.001) return null;
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, len, segments), material);
  mesh.position.copy(new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5));
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  return mesh;
}

// ──────────────────────────────────────────────────────────────────
// mkPipe — Tubería entre dos puntos
// ──────────────────────────────────────────────────────────────────

/**
 * Construye un cilindro tridimensional entre dos puntos (start, end).
 * Color = sistema (AF azul, AC rojo). Grosor = diámetro nominal.
 *
 * Devuelve null si los puntos son coincidentes (longitud < 1 mm) para evitar
 * geometrías degeneradas que rompen el material.
 *
 * @param start   Punto inicial [x, y, z] en metros.
 * @param end     Punto final [x, y, z] en metros.
 * @param system  'AF' o 'AC' → determina el color.
 * @param diam    Diámetro nominal → determina el radio.
 */
export function mkPipe(
  start: Vec3,
  end: Vec3,
  system: System,
  diam: Diameter,
): THREE.Mesh | null {
  const sv = new THREE.Vector3(...start);
  const ev = new THREE.Vector3(...end);
  const dir = new THREE.Vector3().subVectors(ev, sv);
  const len = dir.length();
  if (len < 0.001) return null;

  const center = new THREE.Vector3().addVectors(sv, ev).multiplyScalar(0.5);
  const r = PIPE_RADIUS[diam];
  const color = system === 'AC' ? COLORS.AC : COLORS.AF;

  // Cilindro de tubería + endcap esférico en cada extremo. El endcap evita
  // que los empalmes con codos queden con un "hueco" cuadrado y refuerza
  // visualmente la junta. Material con brillo metálico suave (v1.23).
  const geo = new THREE.CylinderGeometry(r, r, len, 18);
  const mat = new THREE.MeshPhongMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.22,
    shininess: 130,
    specular: 0xffffff,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.copy(center);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  // Cap esférico en cada extremo (mismo radio que el tubo)
  const capGeo = new THREE.SphereGeometry(r, 12, 12);
  const startCap = new THREE.Mesh(capGeo, mat);
  startCap.position.set(0, -len / 2, 0);
  mesh.add(startCap);
  const endCap = new THREE.Mesh(capGeo, mat);
  endCap.position.set(0, len / 2, 0);
  mesh.add(endCap);
  return mesh;
}

// ──────────────────────────────────────────────────────────────────
// mkCodo — Codo curvado de ¼ de vuelta (¼ de torus)
// ──────────────────────────────────────────────────────────────────

/**
 * Codo de cobre curvado de ¼ de vuelta. Se usa para las uniones de 90°
 * entre tubos perpendiculares. La curvatura suave reemplaza la "esferita
 * genérica" que se veía antes (v1.23 — feedback usuario para que el
 * sistema se parezca más a una instalación real).
 *
 * El codo se renderiza orientado en el plano XZ por defecto (plano del
 * suelo). El sceneBuilder lo gira según el `pos` y los tubos adyacentes.
 *
 * @param pos      Posición del codo (centro del cuarto de vuelta).
 * @param color    Color del cuerpo del codo.
 * @param diam     Diámetro nominal (default ½").
 */
export function mkCodo(pos: Vec3, color: number, diam: Diameter = '1/2"'): THREE.Mesh {
  const r = PIPE_RADIUS[diam];
  // Radio de curvatura del codo (≈ 2 × radio del tubo es estándar comercial)
  const curveR = r * 2.4;
  const geo = new THREE.TorusGeometry(curveR, r, 10, 16, Math.PI / 2);
  const mat = new THREE.MeshPhongMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.28,
    shininess: 120,
    specular: 0xffffff,
  });
  const m = new THREE.Mesh(geo, mat);
  m.position.set(...pos);
  return m;
}

/**
 * Codo orientado por las tuberias conectadas. En vez de dejar un cuarto de
 * torus con una rotacion fija, dibuja dos pequenos brazos alineados con los
 * dos tubos que llegan al punto y una union redondeada en la esquina.
 */
export function mkCodoDirected(
  pos: Vec3,
  color: number,
  diam: Diameter = '1/2"',
  dirs: readonly [Vec3, Vec3],
): THREE.Group {
  const r = PIPE_RADIUS[diam];
  const armLen = r * 4.2;
  const g = new THREE.Group();
  const mat = new THREE.MeshPhongMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.32,
    shininess: 120,
    specular: 0xffffff,
  });

  function arm(dir: Vec3): THREE.Mesh | null {
    const dv = new THREE.Vector3(...dir);
    if (dv.length() < 0.001) return null;
    dv.normalize();
    const geo = new THREE.CylinderGeometry(r * 1.12, r * 1.12, armLen, 12);
    const m = new THREE.Mesh(geo, mat);
    m.position.copy(dv.clone().multiplyScalar(armLen / 2));
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dv);
    return m;
  }

  const a = arm(dirs[0]);
  const b = arm(dirs[1]);
  if (a) g.add(a);
  if (b) g.add(b);

  const hub = new THREE.Mesh(new THREE.SphereGeometry(r * 1.45, 12, 12), mat);
  g.add(hub);
  g.position.set(...pos);
  return g;
}

// ──────────────────────────────────────────────────────────────────
// mkTee — Tee de tres vías
// ──────────────────────────────────────────────────────────────────

/**
 * Tee de derivación: dos cilindros perpendiculares cruzados que se ven
 * como una T real (no como una esferita). Por defecto el cilindro
 * principal va en X y la derivación sale en Y.
 */
export function mkTee(pos: Vec3, color: number, diam: Diameter = '1/2"'): THREE.Group {
  const r = PIPE_RADIUS[diam];
  const g = new THREE.Group();
  const mat = new THREE.MeshPhongMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.3,
    shininess: 120,
    specular: 0xffffff,
  });
  // Cuerpo horizontal (pasante, en X)
  const body = new THREE.Mesh(new THREE.CylinderGeometry(r * 1.18, r * 1.18, r * 5, 14), mat);
  body.rotation.z = Math.PI / 2;
  g.add(body);
  // Derivación (perpendicular, en Y)
  const der = new THREE.Mesh(new THREE.CylinderGeometry(r * 1.18, r * 1.18, r * 3, 14), mat);
  der.position.y = r * 1.5;
  g.add(der);
  g.position.set(...pos);
  return g;
}

export function mkTeeDirected(
  pos: Vec3,
  color: number,
  diam: Diameter = '1/2"',
  dirs: readonly Vec3[],
): THREE.Group {
  const r = PIPE_RADIUS[diam] * 1.18;
  const armLen = r * 3.8;
  const g = new THREE.Group();
  const mat = new THREE.MeshPhongMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.3,
    shininess: 120,
    specular: 0xffffff,
  });

  for (const dir of dirs.slice(0, 3)) {
    const dv = new THREE.Vector3(...dir);
    if (dv.length() < 0.001) continue;
    dv.normalize();
    const arm = segmentBetween(
      new THREE.Vector3(0, 0, 0),
      dv.multiplyScalar(armLen),
      r,
      mat,
      14,
    );
    if (arm) g.add(arm);
  }

  const hub = new THREE.Mesh(new THREE.SphereGeometry(r * 1.25, 12, 12), mat);
  g.add(hub);
  g.position.set(...pos);
  return g;
}

// ──────────────────────────────────────────────────────────────────
// mkAcc — Accesorio genérico (codo, tee, unión universal)
// ──────────────────────────────────────────────────────────────────

/**
 * Construye un accesorio simple (esfera) en una posición dada.
 * Usado para codos, tees, uniones — todo lo que no es reducción ni
 * ensamblaje de llave de paso.
 *
 * @param pos    Posición [x, y, z] en metros.
 * @param color  Color hex (típicamente COLORS.acc o COLORS.acc_ac).
 */
export function mkAcc(pos: Vec3, color: number): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.SphereGeometry(0.038, 10, 10),
    new THREE.MeshPhongMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.55,
    }),
  );
  m.position.set(...pos);
  return m;
}

/**
 * Construye el cabezal de regadera de la ducha apuntando hacia abajo (§VI
 * altura `ducha_brazo` ≈ 2.05 m). La pos[1] es la altura del brazo
 * horizontal; el plato del cabezal cuelga UN POCO por debajo para que
 * visualmente se vea como una regadera real, no como una esfera flotante.
 */
export function mkRegadera(pos: Vec3, color: number = 0xff8c42): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshPhongMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.5,
    shininess: 80,
  });
  // Cuello vertical corto que conecta el brazo al plato
  const cuello = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.06, 10), mat);
  cuello.position.y = -0.03;
  g.add(cuello);
  // Plato del cabezal (cono truncado ancho apuntando hacia abajo)
  const plato = new THREE.Mesh(
    new THREE.CylinderGeometry(0.055, 0.085, 0.04, 22),
    mat,
  );
  plato.position.y = -0.08;
  g.add(plato);
  // Borde del plato (pequeño disco para la "cara" del cabezal)
  const cara = new THREE.Mesh(new THREE.CylinderGeometry(0.085, 0.085, 0.005, 22), mat);
  cara.position.y = -0.10;
  g.add(cara);
  g.position.set(...pos);
  return g;
}

// ──────────────────────────────────────────────────────────────────
// mkReduction — Bushing / cono reductor
// ──────────────────────────────────────────────────────────────────

/**
 * Construye un cono que representa una reducción (bushing) 3/4"→1/2"
 * o 1/2"→3/4". El cono va de un radio a otro a lo largo de su eje Y.
 *
 * @param pos     Posición [x, y, z].
 * @param from34  Si true (default), reduce de 3/4" a 1/2".
 *                Si false, expande de 1/2" a 3/4".
 */
export function mkReduction(pos: Vec3, from34 = true): THREE.Mesh {
  const r1 = from34 ? 0.026 : 0.018;
  const r2 = from34 ? 0.018 : 0.026;
  const geo = new THREE.CylinderGeometry(r2, r1, 0.045, 8);
  const mat = new THREE.MeshPhongMaterial({
    color: COLORS.acc,
    emissive: COLORS.acc,
    emissiveIntensity: 0.6,
    shininess: 100,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(...pos);
  return mesh;
}

export function mkReductionDirected(
  pos: Vec3,
  color: number,
  dirs: readonly Vec3[],
  from34 = true,
): THREE.Group {
  const rBig = from34 ? 0.03 : 0.021;
  const rSmall = from34 ? 0.021 : 0.03;
  const len = 0.085;
  const axis = new THREE.Vector3(...(dirs[0] ?? [1, 0, 0]));
  if (axis.length() < 0.001) axis.set(1, 0, 0);
  axis.normalize();

  const mat = new THREE.MeshPhongMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.65,
    shininess: 110,
    specular: 0xffffff,
  });
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CylinderGeometry(rSmall, rBig, len, 14), mat);
  body.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis);
  g.add(body);

  const ringGeo = new THREE.TorusGeometry(rBig * 1.08, 0.004, 8, 18);
  const ringA = new THREE.Mesh(ringGeo, mat);
  const ringB = new THREE.Mesh(ringGeo, mat);
  ringA.position.copy(axis.clone().multiplyScalar(-len / 2));
  ringB.position.copy(axis.clone().multiplyScalar(len / 2));
  ringA.quaternion.copy(body.quaternion);
  ringB.quaternion.copy(body.quaternion);
  g.add(ringA, ringB);
  g.position.set(...pos);
  return g;
}

// ──────────────────────────────────────────────────────────────────
// mkLlavePaso — Ensamblaje en U completo §VII
// ──────────────────────────────────────────────────────────────────

/**
 * Construye el ensamblaje en U de una llave de paso (§VII baseline).
 *
 * Composición: 2 verticales de subida + 2 horizontales laterales + 4 niples
 * + 2 uniones universales (cajas) + 1 válvula (caja central) + volante de
 * maniobra arriba + 4 codos (esferas en las esquinas).
 *
 * El eje del ensamblaje queda horizontal sobre la tubería principal, con la
 * válvula 10 cm más arriba que la alimentación (rise=0.10 m).
 *
 * @param basePos  Posición [x, y, z] del centro del ensamblaje en su base
 *                 (la altura `y` es la de la tubería principal).
 * @param system   'AF' o 'AC' — determina el color de las tuberías del
 *                 ensamblaje. La válvula siempre va en color dorado COLORS.valve.
 */
export function mkLlavePaso(basePos: Vec3, system: System): THREE.Group {
  const g = new THREE.Group();
  const color = system === 'AC' ? COLORS.AC : COLORS.AF;
  const valveColor = COLORS.valve;
  const accColor = COLORS.acc;
  const r = 0.018; // tubería 1/2" del ensamblaje

  // Helpers internos
  function seg(s: Vec3, e: Vec3, c: number, radius = r): THREE.Mesh | null {
    const sv = new THREE.Vector3(...s);
    const ev = new THREE.Vector3(...e);
    const dir = new THREE.Vector3().subVectors(ev, sv);
    const len = dir.length();
    if (len < 0.001) return null;
    const cen = new THREE.Vector3().addVectors(sv, ev).multiplyScalar(0.5);
    const mat = new THREE.MeshPhongMaterial({
      color: c,
      emissive: c,
      emissiveIntensity: 0.35,
      shininess: 80,
    });
    const m = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, len, 8), mat);
    m.position.copy(cen);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
    return m;
  }
  function ball(p: Vec3, c: number, rad = 0.028): THREE.Mesh {
    const m = new THREE.Mesh(
      new THREE.SphereGeometry(rad, 8, 8),
      new THREE.MeshPhongMaterial({ color: c, emissive: c, emissiveIntensity: 0.55 }),
    );
    m.position.set(...p);
    return m;
  }
  function box(p: Vec3, w: number, h: number, d: number, c: number): THREE.Mesh {
    const m = new THREE.Mesh(
      new THREE.BoxGeometry(w, h, d),
      new THREE.MeshPhongMaterial({ color: c, emissive: c, emissiveIntensity: 0.5 }),
    );
    m.position.set(...p);
    return m;
  }

  // Dimensiones del ensamblaje (coordenadas locales)
  const rise = 0.1; // sube 10 cm sobre la alimentación
  const horiz = 0.08; // tramo horizontal antes de la UU
  const nipL = 0.035; // niple
  const uuL = 0.025; // unión universal (disco)
  const valW = 0.07; // largo de la válvula

  const y0 = 0;
  const y1 = rise;
  const x1 = horiz;
  const x2 = x1 + nipL;
  const x3 = x2 + uuL;
  const x4 = x3 + nipL;
  const xv = x4;
  const xv2 = xv + valW;
  const x5 = xv2 + nipL;
  const x6 = x5 + uuL;
  const x7 = x6 + nipL;
  const x8 = x7 + horiz;
  const halfW = x8 / 2;

  // Subidas verticales (desde tubería principal)
  const lft = seg([0, y0, 0], [0, y1, 0], color);
  const rgt = seg([x8, y0, 0], [x8, y1, 0], color);
  if (lft) g.add(lft);
  if (rgt) g.add(rgt);

  // Horizontales laterales
  const hl = seg([0, y1, 0], [x1, y1, 0], color);
  const hr = seg([x7, y1, 0], [x8, y1, 0], color);
  if (hl) g.add(hl);
  if (hr) g.add(hr);

  // Niples junto a UU
  const nl = seg([x1, y1, 0], [x2, y1, 0], color);
  const nr = seg([x5, y1, 0], [x6, y1, 0], color);
  const nrr = seg([x6, y1, 0], [x7, y1, 0], color);
  if (nl) g.add(nl);
  if (nr) g.add(nr);
  if (nrr) g.add(nrr);

  // Uniones universales (cajas ámbar)
  g.add(box([x2 + uuL / 2, y1, 0], 0.012, r * 2.5, 0.042, accColor));
  g.add(box([x5 + uuL / 2, y1, 0], 0.012, r * 2.5, 0.042, accColor));

  // Niples entre UU y válvula
  const nv1 = seg([x3, y1, 0], [x4, y1, 0], color);
  const nv2 = seg([xv2, y1, 0], [x5, y1, 0], color);
  if (nv1) g.add(nv1);
  if (nv2) g.add(nv2);

  // VÁLVULA central (cuerpo dorado)
  g.add(box([xv + valW / 2, y1, 0], valW, r * 3, 0.05, valveColor));
  // Vástago (stem) hacia arriba
  const stem = new THREE.Mesh(
    new THREE.CylinderGeometry(0.006, 0.006, 0.06, 8),
    new THREE.MeshPhongMaterial({ color: 0xffd60a, emissive: 0xffd60a, emissiveIntensity: 0.35 }),
  );
  stem.position.set(xv + valW / 2, y1 + 0.04, 0);
  g.add(stem);
  // Volante de maniobra (torus dorado, apunta hacia arriba §VII)
  const wheel = new THREE.Mesh(
    new THREE.TorusGeometry(0.04, 0.007, 8, 18),
    new THREE.MeshPhongMaterial({ color: 0xffd60a, emissive: 0xffd60a, emissiveIntensity: 0.45 }),
  );
  wheel.rotation.x = Math.PI / 2;
  wheel.position.set(xv + valW / 2, y1 + 0.07, 0);
  g.add(wheel);

  // Codos (esferas en las 4 esquinas)
  [0, x8].forEach((x) => g.add(ball([x, y0, 0], accColor, 0.022)));
  [0, x8].forEach((x) => g.add(ball([x, y1, 0], accColor, 0.022)));

  // Centrar el ensamblaje en basePos
  g.position.set(basePos[0] - halfW, basePos[1], basePos[2]);
  return g;
}

// ──────────────────────────────────────────────────────────────────
// mkValve — Válvula simple (no es llave de paso)
// ──────────────────────────────────────────────────────────────────

/**
 * Válvula inline simple (sin ensamblaje en U). Para casos donde la válvula
 * va directamente en la línea sin desviación. NO usar para "llave de paso"
 * (eso es mkLlavePaso §VII).
 */
export function mkValve(pos: Vec3, color: number = COLORS.valve): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.07, 0.05, 0.05),
    new THREE.MeshPhongMaterial({ color, emissive: color, emissiveIntensity: 0.5 }),
  );
  g.add(body);
  const handle = new THREE.Mesh(
    new THREE.CylinderGeometry(0.005, 0.005, 0.065, 6),
    new THREE.MeshPhongMaterial({ color, emissive: color, emissiveIntensity: 0.4 }),
  );
  handle.position.y = 0.054;
  g.add(handle);
  g.position.set(...pos);
  return g;
}

// ──────────────────────────────────────────────────────────────────
// mkSanitary — Aparato sanitario estilo Revit (modelo sólido por tipo)
// ──────────────────────────────────────────────────────────────────

/**
 * Construye el aparato sanitario como un modelo 3D SÓLIDO y reconocible
 * (estilo Revit/BIM), NO una caja esquemática. Decisión MVP: el usuario
 * quiere ver inodoro/lavatorio/ducha como objetos, no como wireframes.
 *
 * Opacidad 0.85 (§XV): el aparato es casi sólido pero deja entrever las
 * tuberías que pasan por detrás.
 *
 * Convención de posición (corrige la orientación):
 *  - `s.pos = [centerX, 0, centerZ]`: el aparato se centra en x,z con la base
 *    en y=0. El MURO trasero está en z=0, así que el "fondo" del aparato
 *    (tanque del inodoro, respaldo del lavatorio, panel de la ducha) va hacia
 *    z MENOR (contra el muro) y el frente hacia z MAYOR (interior del cuarto).
 *
 * Solo primitivas r128: Box / Cylinder / Sphere / Cone / Torus
 * (CapsuleGeometry PROHIBIDA, §III). Todos los meshes son hijos DIRECTOS del
 * Group para que sceneBuilder propague userData (clickability) a cada parte.
 */
export function mkSanitary(s: Sanitary): THREE.Group {
  const g = new THREE.Group();
  const cx = s.pos[0];
  const cz = s.pos[2];
  const backZ = cz - s.d / 2; // lado contra el muro
  const frontZ = cz + s.d / 2; // lado hacia el interior

  const porcelain = (): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({
      color: 0xd7e3ec, // porcelana clara
      roughness: 0.3,
      metalness: 0.05,
      transparent: true,
      opacity: SANITARY_OPACITY,
      emissive: 0x0b1a28,
      emissiveIntensity: 0.18,
    });
  const glass = (): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({
      color: 0x8fbfe0,
      roughness: 0.1,
      metalness: 0.0,
      transparent: true,
      opacity: 0.16,
      emissive: 0x10304a,
      emissiveIntensity: 0.3,
    });
  const chrome = (): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({
      color: 0xc8d2da,
      roughness: 0.18,
      metalness: 0.85,
    });

  function add(mesh: THREE.Mesh): void {
    g.add(mesh);
  }
  function box(w: number, h: number, d: number, x: number, y: number, z: number, m: THREE.Material): void {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
    mesh.position.set(x, y, z);
    add(mesh);
  }
  function cyl(rt: number, rb: number, h: number, x: number, y: number, z: number, m: THREE.Material, seg = 20): void {
    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, seg), m);
    mesh.position.set(x, y, z);
    add(mesh);
  }

  switch (s.type) {
    case 'Inodoro': {
      // Tanque (cisterna) contra el muro
      const tankD = s.d * 0.22;
      box(s.w * 0.94, 0.42, tankD, cx, 0.53, backZ + tankD / 2, porcelain());
      // Tapa de la cisterna
      box(s.w * 1.0, 0.04, tankD * 1.15, cx, 0.76, backZ + tankD / 2, porcelain());
      // Pedestal del cuenco
      cyl(0.1, 0.14, 0.4, cx, 0.2, cz + s.d * 0.06, porcelain(), 18);
      // Cuenco (taza) — cilindro ancho ligeramente elíptico hacia el frente
      const bowl = new THREE.Mesh(
        new THREE.CylinderGeometry(0.19, 0.15, 0.2, 22),
        porcelain(),
      );
      bowl.position.set(cx, 0.42, frontZ - s.d * 0.26);
      bowl.scale.z = 1.25; // alarga la taza hacia el frente
      add(bowl);
      // Asiento (anillo aplanado sobre la taza)
      const seat = new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.035, 10, 22), porcelain());
      seat.rotation.x = Math.PI / 2;
      seat.position.set(cx, 0.52, frontZ - s.d * 0.26);
      seat.scale.z = 1.25;
      add(seat);
      break;
    }
    case 'Lavatorio': {
      // Pedestal cónico
      cyl(0.07, 0.16, 0.78, cx, 0.39, cz, porcelain(), 18);
      // Cuenco (basin) — cilindro ancho abierto + borde
      cyl(0.26, 0.18, 0.16, cx, 0.86, cz, porcelain(), 24);
      box(s.w, 0.03, s.d * 0.9, cx, 0.95, cz, porcelain());
      // Respaldo (backsplash) contra el muro
      box(s.w, 0.16, 0.03, cx, 0.96, backZ + 0.02, porcelain());
      // Grifería (caño cromado) hacia el muro
      cyl(0.015, 0.015, 0.14, cx, 1.0, backZ + 0.06, chrome(), 10);
      const spout = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.1, 10), chrome());
      spout.rotation.x = Math.PI / 2;
      spout.position.set(cx, 1.06, backZ + 0.11);
      add(spout);
      break;
    }
    case 'Ducha': {
      // Discriminar por subtype: tina/bañera tiene geometría distinta a
      // la ducha estándar (plato + mamparas).
      const isTina =
        s.subtype.toLowerCase().includes('tina') ||
        s.subtype.toLowerCase().includes('bañera');

      if (isTina) {
        // Tina típica peruana: caja exterior blanca con borde redondeado
        // visual y poza interior hundida. Altura del borde ~50 cm.
        const tinaH = 0.52;
        // Cuerpo exterior (cuerpo de la bañera)
        box(s.w, tinaH, s.d, cx, tinaH / 2, cz, porcelain());
        // Poza interior (vaciado) — caja ligeramente más pequeña y hundida
        const wallT = 0.06; // espesor del borde
        const innerW = s.w - wallT * 2;
        const innerD = s.d - wallT * 2;
        const innerH = tinaH - 0.08;
        box(innerW, innerH, innerD, cx, tinaH - innerH / 2 + 0.02, cz, porcelain());
        // Curvas en las 4 esquinas superiores (1/4 cilindros)
        const cornerR = 0.04;
        const cornerY = tinaH - cornerR;
        const corners: [number, number][] = [
          [cx - s.w / 2 + cornerR, cz - s.d / 2 + cornerR],
          [cx + s.w / 2 - cornerR, cz - s.d / 2 + cornerR],
          [cx - s.w / 2 + cornerR, cz + s.d / 2 - cornerR],
          [cx + s.w / 2 - cornerR, cz + s.d / 2 - cornerR],
        ];
        for (const [px, pz] of corners) {
          const torus = new THREE.Mesh(
            new THREE.TorusGeometry(cornerR, 0.012, 6, 8, Math.PI / 2),
            porcelain(),
          );
          torus.rotation.x = Math.PI / 2;
          // orientar cada torus para apuntar la curva hacia afuera de la esquina
          const dx = px > cx ? 1 : -1;
          const dz = pz > cz ? 1 : -1;
          torus.rotation.z = dx === 1 && dz === 1 ? 0 : dx === -1 && dz === 1 ? Math.PI / 2 : dx === -1 && dz === -1 ? Math.PI : -Math.PI / 2;
          torus.position.set(px, cornerY, pz);
          add(torus);
        }
        // Grifería de la tina: caño + 2 llaves + tubo horizontal entre
        // ellas se genera en applyStrategy (no en el modelo del aparato)
        // para que conecte con los tubos AF/AC reales y mantenga
        // coherencia visual con la altura de la grifería.
        break;
      }

      // Ducha estándar: plato + mamparas. El brazo + cabezal NO van en
      // el modelo del aparato — los genera applyStrategy (`mkRegadera`)
      // para que coincidan con el tubo real que sube de la T mezcladora.
      box(s.w, 0.05, s.d, cx, 0.025, cz, porcelain());
      // Panel posterior (contra el muro)
      box(s.w, s.h * 0.96, 0.03, cx, (s.h * 0.96) / 2, backZ + 0.02, glass());
      // Mampara lateral (lado del interior)
      box(0.03, s.h * 0.96, s.d, cx - s.w / 2 + 0.02, (s.h * 0.96) / 2, cz, glass());
      break;
    }
    case 'Urinario': {
      box(s.w * 0.8, s.h * 0.6, s.d * 0.5, cx, s.h * 0.55, backZ + s.d * 0.28, porcelain());
      const lip = new THREE.Mesh(
        new THREE.CylinderGeometry(s.w * 0.34, s.w * 0.34, s.d * 0.5, 18, 1, false, 0, Math.PI),
        porcelain(),
      );
      lip.rotation.x = Math.PI / 2;
      lip.position.set(cx, s.h * 0.32, backZ + s.d * 0.28);
      add(lip);
      break;
    }
    default: {
      // Lavaderos y otros: base sólida + poza hundida
      box(s.w, s.h * 0.6, s.d, cx, s.h * 0.3, cz, porcelain());
      box(s.w * 0.5, s.h * 0.08, s.d * 0.7, cx - s.w * 0.2, s.h * 0.6, cz, porcelain());
    }
  }

  return g;
}
